import React, { useId } from 'react';

import type { ITextAreaProps } from './TextArea.types';

/**
 * A multi-line text field, styled as Input is: a label above, the field, and a
 * line under it with the helper or error text and an optional counter at its
 * end. The field grows only when the user drags its corner.
 */
export const TextArea: React.FC<ITextAreaProps> = ({
  label,
  helperText,
  errorText,
  counter,
  className = '',
  containerClassName = '',
  labelClassName = '',
  id,
  rows = 4,
  disabled = false,
  ...textAreaProps
}: ITextAreaProps) => {
  const generatedId = useId();
  const textAreaId = id || generatedId;
  const noteId = `${textAreaId}-note`;
  const note = errorText || helperText;
  const hasNoteLine = !!note || !!counter;

  const borderClasses = errorText
    ? 'border-border-error focus:border-border-error'
    : 'border-border-secondary focus:border-border-primary-hover';

  const textAreaClasses = `
    w-full
    px-bg-v2-lg
    py-bg-v2-md
    text-bg-v2-md
    font-bg-v2-sans
    font-bg-v2-regular
    border-2
    ${borderClasses}
    bg-bg-primary
    text-text-primary
    placeholder:text-text-placeholder
    rounded-bg-v2-md
    resize-y
    transition-colors
    duration-200
    focus:outline-none
    disabled:bg-bg-disabled
    disabled:text-text-disabled
    disabled:border-border-disabled
    disabled:cursor-not-allowed
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const noteColor = errorText ? 'text-text-error-primary' : 'text-text-tertiary';

  return (
    <div className={`flex flex-col gap-bg-v2-sm w-full ${containerClassName}`}>
      {label && (
        <label
          htmlFor={textAreaId}
          className={`font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary ${labelClassName}`}
        >
          {label}
        </label>
      )}

      <textarea
        id={textAreaId}
        rows={rows}
        disabled={disabled}
        aria-invalid={errorText ? true : undefined}
        aria-describedby={hasNoteLine ? noteId : undefined}
        className={textAreaClasses}
        {...textAreaProps}
      />

      {hasNoteLine && (
        <div id={noteId} className="flex items-start justify-between gap-bg-v2-md">
          <span className={`font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm ${noteColor}`}>
            {note}
          </span>
          {counter && (
            <span className={`shrink-0 font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm ${noteColor}`}>
              {counter}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
